import Phaser from 'phaser'
import { DEPTH_ZONES, FILLER_TILES } from '../data/tiles'
import { ORE_TILES } from '../data/tiles'
import { createGridGenerator } from '../systems/GridGenerator'
import type { GridGenerator } from '../systems/GridGenerator'
import * as GS from '../systems/GameState'
import { getPublicKey } from '../chain/wallet'
import { fetchPlayerState } from '../chain/player'
import { sendDrillSteps, finishRun as chainFinishRun, fetchRunState, undelegateRun, waitForUndelegation } from '../chain/run'
import { getCluster, getConnection } from '../chain/connection'
import { cachePlayerState, cacheRunState } from '../systems/GameState'
import type { OnChainRunState, CargoItem as ChainCargoItem } from '../chain/run'
import type { AggregatedOreMap } from '../types/game'
import type { GridCell, DepthZone } from '../types/tiles'

const GAME_W = 720
const GAME_H = 1280

const TILE_W = 144
const TILE_H = 144
const GRID_COLS = 5
const GRID_LEFT = (GAME_W - GRID_COLS * TILE_W) / 2
const VISIBLE_ROWS = 14

const STATS_H = 100
const ANIMATE_DELAY = 500 // ms per tile during animation
const BATCH_SIZE = 10

/** Maps sprite filename prefix → game tile ID for filler tiles */
const FILLER_SPRITE_MAP: Record<string, string> = {
  dirt: 'dirt',
  clay: 'clay',
  sandstone: 'stone',
  stone: 'hardrock',
  marble: 'basalt',
  granite: 'granite',
}

/** Maps ore game ID → sprite filename prefix */
const ORE_SPRITE_MAP: Record<string, string> = {
  copper: 'copper',
  iron: 'iron',
  silver: 'silver',
  gold: 'gold',
  platinum: 'cinnabar',
  emerald: 'emerald',
  ruby: 'ruby',
  diamond: 'diamond',
}

const FILLER_IDS = new Set(FILLER_TILES.map(t => t.id))

interface DrillSceneData {
  column: number
  seed: number
  runState: OnChainRunState
}

interface BatchResult {
  fromDepth: number
  toDepth: number
  fromFuel: number
  toFuel: number
  fromCargo: number
  toCargo: number
  cargo: ChainCargoItem[]
  ended: boolean
}

export default class DrillScene extends Phaser.Scene {
  private column!: number
  private seed!: number
  private displayDepth!: number // what we've animated up to
  private maxFuel!: number
  private maxCargo!: number
  private luckMult!: number
  private sellMult!: number
  private finished!: boolean
  private gridGen!: GridGenerator
  private generatedRows!: GridCell[][]
  private gridContainer!: Phaser.GameObjects.Container
  private drillMarker!: Phaser.GameObjects.Sprite
  private depthText!: Phaser.GameObjects.Text
  private fuelText!: Phaser.GameObjects.Text
  private cargoText!: Phaser.GameObjects.Text
  private zoneText!: Phaser.GameObjects.Text

  // Display state (what the player sees, updated by animation loop)
  private displayFuel!: number
  private displayCargo!: number
  private latestCargo!: ChainCargoItem[]

  // Pipeline queue: chain loop pushes, animation loop consumes
  private batchQueue: BatchResult[] = []
  private chainDone = false

  constructor() {
    super('DrillScene')
  }

  init(data: DrillSceneData): void {
    this.column = data.column
    this.seed = data.seed

    const rs = data.runState
    this.displayDepth = rs.depth
    this.displayFuel = rs.fuel
    this.displayCargo = rs.cargoWeight
    this.maxFuel = rs.maxFuel
    this.maxCargo = rs.maxCargo
    this.latestCargo = [...rs.cargo]
    this.luckMult = rs.luckMult
    this.sellMult = rs.sellMult
    this.batchQueue = []
    this.chainDone = false
  }

  preload(): void {
    for (const [spritePrefix, gameId] of Object.entries(FILLER_SPRITE_MAP)) {
      for (let v = 1; v <= 4; v++) {
        this.load.image(`tile_${gameId}_${v}`, `tiles/${spritePrefix}_${v}.png`)
      }
    }
    for (const [gameId, spritePrefix] of Object.entries(ORE_SPRITE_MAP)) {
      for (let v = 1; v <= 2; v++) {
        this.load.image(`ore_${gameId}_${v}`, `tiles/${spritePrefix}_${v}.png`)
      }
    }
    this.load.spritesheet('drill', 'drill.png', { frameWidth: 64, frameHeight: 64 })
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x111111)
    this.finished = false

    // Devnet banner
    if (getCluster() === 'devnet') {
      this.add.rectangle(GAME_W / 2, GAME_H - 16, GAME_W, 32, 0xcc0000).setDepth(999)
      this.add.text(GAME_W / 2, GAME_H - 16, 'DEVNET', {
        fontSize: '18px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(1000)
    }

    // Grid generation (deterministic, matching on-chain)
    console.log(`[init] seed=${this.seed} column=${this.column} luckMult=${this.luckMult} sellMult=${this.sellMult} maxFuel=${this.maxFuel} maxCargo=${this.maxCargo}`)
    this.gridGen = createGridGenerator(this.seed, this.luckMult)
    this.generatedRows = []

    // Graphics container
    this.gridContainer = this.add.container(0, STATS_H)

    // Pre-generate initial rows
    for (let i = 0; i < VISIBLE_ROWS + 5; i++) {
      this.generateAndCacheRow(i)
    }

    this.drawGrid()

    // Drill marker (animated sprite)
    this.anims.create({
      key: 'drill_anim',
      frames: this.anims.generateFrameNumbers('drill', { start: 0, end: 15 }),
      frameRate: 16 / (ANIMATE_DELAY / 1000),
      repeat: -1,
    })
    this.drillMarker = this.add.sprite(
      GRID_LEFT + this.column * TILE_W + TILE_W / 2,
      STATS_H + TILE_H / 2,
      'drill'
    ).setDisplaySize(TILE_W, TILE_H).play('drill_anim')

    // Stats bar
    this.add.rectangle(GAME_W / 2, STATS_H / 2, GAME_W, STATS_H, 0x000000, 0.85)
    this.depthText = this.add.text(20, 16, '', { fontSize: '28px', fontFamily: 'monospace', color: '#FFFFFF' })
    this.fuelText = this.add.text(20, 56, '', { fontSize: '28px', fontFamily: 'monospace', color: '#FFAA00' })
    this.cargoText = this.add.text(GAME_W - 20, 16, '', { fontSize: '28px', fontFamily: 'monospace', color: '#88CCFF' }).setOrigin(1, 0)
    this.zoneText = this.add.text(GAME_W - 20, 56, '', { fontSize: '24px', fontFamily: 'monospace', color: '#AAAAAA' }).setOrigin(1, 0)
    this.updateStats()

    // Start both loops concurrently
    this.chainLoop()
    this.animationLoop()
  }

  private generateAndCacheRow(rowIndex: number): GridCell[] {
    if (!this.generatedRows[rowIndex]) {
      this.generatedRows[rowIndex] = this.gridGen.generateRow(rowIndex)
    }
    return this.generatedRows[rowIndex]
  }

  private getZone(depth: number): DepthZone {
    let zone = DEPTH_ZONES[0]
    for (const z of DEPTH_ZONES) {
      if (depth >= z.minDepth) zone = z
    }
    return zone
  }

  private getFillerIdForDepth(depth: number): string {
    const candidates = FILLER_TILES.filter(t => depth >= t.minDepth && depth <= t.maxDepth)
    if (candidates.length === 0) return FILLER_TILES[FILLER_TILES.length - 1].id
    return candidates[Math.floor(candidates.length / 2)].id
  }

  private drawGrid(): void {
    this.gridContainer.removeAll(true)

    const startRow = Math.max(0, this.displayDepth - 2)
    const endRow = startRow + VISIBLE_ROWS

    for (let row = startRow; row < endRow; row++) {
      this.generateAndCacheRow(row)
      const rowData = this.generatedRows[row]
      if (!rowData) continue

      const visualY = (row - startRow) * TILE_H

      for (let col = 0; col < GRID_COLS; col++) {
        const cell = rowData[col]
        const x = GRID_LEFT + col * TILE_W
        const cx = x + TILE_W / 2
        const cy = visualY + TILE_H / 2

        if (FILLER_IDS.has(cell.tile.id)) {
          // Filler tile: render sprite
          const variant = (row * 7 + col * 13) % 4 + 1
          const key = `tile_${cell.tile.id}_${variant}`
          const img = this.add.image(cx, cy, key)
            .setDisplaySize(TILE_W, TILE_H)
          this.gridContainer.add(img)
        } else {
          // Ore tile: filler background + ore sprite overlay
          const fillerId = this.getFillerIdForDepth(row)
          const fillerVariant = (row * 7 + col * 13) % 4 + 1
          const bg = this.add.image(cx, cy, `tile_${fillerId}_${fillerVariant}`)
            .setDisplaySize(TILE_W, TILE_H)
          this.gridContainer.add(bg)

          const oreVariant = (row * 3 + col * 11) % 2 + 1
          const ore = this.add.image(cx, cy, `ore_${cell.tile.id}_${oreVariant}`)
            .setDisplaySize(TILE_W, TILE_H)
          this.gridContainer.add(ore)
        }
      }
    }

    // Update drill marker position
    const drillVisualRow = this.displayDepth - Math.max(0, this.displayDepth - 2)
    if (this.drillMarker) {
      this.drillMarker.setY(STATS_H + drillVisualRow * TILE_H + TILE_H / 2)
    }

    const zone = this.getZone(this.displayDepth)
    this.cameras.main.setBackgroundColor(zone.bgColor)
  }

  /**
   * Chain loop: continuously sends batches on-chain and pushes results to the queue.
   * Runs independently from the animation loop.
   */
  private async chainLoop(): Promise<void> {
    let trackDepth = this.displayDepth
    let trackFuel = this.displayFuel
    let trackCargo = this.displayCargo

    while (!this.finished) {
      try {
        await sendDrillSteps(BATCH_SIZE)

        await this.sleep(300)
        const pubkey = getPublicKey()!
        const runState = await fetchRunState(pubkey, true)
        if (!runState) {
          this.chainDone = true
          return
        }

        const ended = runState.fuel === 0
          || runState.cargoWeight >= runState.maxCargo
          || runState.depth === trackDepth

        this.batchQueue.push({
          fromDepth: trackDepth,
          toDepth: runState.depth,
          fromFuel: trackFuel,
          toFuel: runState.fuel,
          fromCargo: trackCargo,
          toCargo: runState.cargoWeight,
          cargo: [...runState.cargo],
          ended,
        })

        trackDepth = runState.depth
        trackFuel = runState.fuel
        trackCargo = runState.cargoWeight

        if (ended) {
          this.chainDone = true
          return
        }
      } catch (err) {
        console.error('Chain loop error:', err)
        this.chainDone = true
        return
      }
    }
  }

  /**
   * Animation loop: consumes batch results from the queue and animates
   * at ANIMATE_DELAY ms per tile. Runs concurrently with the chain loop.
   */
  private async animationLoop(): Promise<void> {
    while (!this.finished) {
      if (this.batchQueue.length > 0) {
        const batch = this.batchQueue.shift()!
        const steps = batch.toDepth - batch.fromDepth

        console.log(`[batch] depth ${batch.fromDepth}→${batch.toDepth}, cargo:`, batch.cargo.map(c => `${ORE_TILES[c.oreIndex]?.name ?? '?'}×${c.count}`))

        if (steps > 0) {
          for (let d = batch.fromDepth; d < batch.toDepth; d++) {
            if (this.finished) return

            // Log what tile the drill is passing through in its column
            this.generateAndCacheRow(d)
            const drillCell = this.generatedRows[d]?.[this.column]
            if (drillCell && !FILLER_IDS.has(drillCell.tile.id)) {
              console.log(`[drill] depth ${d} col ${this.column}: ORE ${drillCell.tile.name} (id=${drillCell.tile.id})`)
            }

            const progress = (d - batch.fromDepth + 1) / steps
            this.displayDepth = d + 1
            this.displayFuel = Math.round(batch.fromFuel + (batch.toFuel - batch.fromFuel) * progress)
            this.displayCargo = Math.round(batch.fromCargo + (batch.toCargo - batch.fromCargo) * progress)

            for (let i = this.displayDepth; i < this.displayDepth + VISIBLE_ROWS + 5; i++) {
              this.generateAndCacheRow(i)
            }

            this.drawGrid()
            this.updateStats()

            await this.sleep(ANIMATE_DELAY)
          }
        }

        this.latestCargo = batch.cargo

        if (batch.ended) {
          this.handleFinish()
          return
        }
      } else if (this.chainDone) {
        // Chain stopped and queue is empty
        this.handleFinish()
        return
      } else {
        // Queue empty, chain still running — wait briefly
        await this.sleep(50)
      }
    }
  }

  /**
   * Called when the run ends (fuel 0 or cargo full).
   * Sends finishRun on-chain, then shows results.
   */
  private async handleFinish(): Promise<void> {
    if (this.finished) return
    this.finished = true

    try {
      const pubkey = getPublicKey()!
      // Undelegate RunState from ER back to base layer
      await undelegateRun()
      await waitForUndelegation(pubkey)

      // Fetch definitive cargo BEFORE finishRun clears it on-chain
      const finalRun = await fetchRunState(pubkey)
      if (finalRun) {
        this.latestCargo = [...finalRun.cargo]
      }

      // Send finishRun on the base layer and wait for confirmation
      const sig = await chainFinishRun()
      await getConnection().confirmTransaction(sig, 'confirmed')

      // Re-fetch player state (cash updated)
      const ps = await fetchPlayerState(pubkey)
      if (ps) cachePlayerState(ps)

      const runState = await fetchRunState(pubkey)
      cacheRunState(runState)

    } catch (err) {
      console.error('Finish run error:', err)
    }

    // Calculate display values from cargo we tracked
    this.showResults()
  }

  private showResults(): void {
    const aggregated: AggregatedOreMap = {}
    let totalValue = 0

    console.log('[results] latestCargo:', JSON.stringify(this.latestCargo))
    for (const item of this.latestCargo) {
      const ore = ORE_TILES[item.oreIndex]
      console.log(`[results] oreIndex=${item.oreIndex} count=${item.count} → ${ore?.name ?? 'UNKNOWN'}`)
      if (!ore) continue
      const sellValue = Math.floor(ore.value * item.count * this.sellMult / 100)
      if (!aggregated[ore.id]) {
        aggregated[ore.id] = { name: ore.name, count: 0, totalValue: 0 }
      }
      aggregated[ore.id].count += item.count
      aggregated[ore.id].totalValue += sellValue
      totalValue += sellValue
    }

    // Dim background
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.85)

    const centerX = GAME_W / 2
    let y = 200

    this.add.text(centerX, y, 'RUN COMPLETE', {
      fontSize: '40px', fontFamily: 'monospace', color: '#FFD700', fontStyle: 'bold',
    }).setOrigin(0.5)

    y += 70

    this.add.text(centerX, y, `Depth reached: ${this.displayDepth}`, {
      fontSize: '26px', fontFamily: 'monospace', color: '#FFFFFF',
    }).setOrigin(0.5)

    y += 60

    const oreKeys = Object.keys(aggregated)
    if (oreKeys.length > 0) {
      this.add.text(centerX, y, '-- Ore Found --', {
        fontSize: '24px', fontFamily: 'monospace', color: '#AAAAAA',
      }).setOrigin(0.5)
      y += 44

      for (const key of oreKeys) {
        const a = aggregated[key]
        this.add.text(centerX, y, `${a.name} x${a.count} = $${a.totalValue}`, {
          fontSize: '24px', fontFamily: 'monospace', color: '#CCCCCC',
        }).setOrigin(0.5)
        y += 38
      }
    } else {
      this.add.text(centerX, y, 'No ore found', {
        fontSize: '24px', fontFamily: 'monospace', color: '#888888',
      }).setOrigin(0.5)
    }

    y += 50

    this.add.text(centerX, y, `Total earned: $${totalValue}`, {
      fontSize: '30px', fontFamily: 'monospace', color: '#00FF00', fontStyle: 'bold',
    }).setOrigin(0.5)

    y += 50

    this.add.text(centerX, y, `Cash: $${GS.getCash()}`, {
      fontSize: '26px', fontFamily: 'monospace', color: '#00CC00',
    }).setOrigin(0.5)

    y += 80

    const continueBtn = this.add.rectangle(centerX, y, 400, 72, 0x444466)
      .setInteractive({ useHandCursor: true })
      .setStrokeStyle(3, 0x8888AA)
    this.add.text(centerX, y, 'CONTINUE', {
      fontSize: '30px', fontFamily: 'monospace', color: '#FFFFFF', fontStyle: 'bold',
    }).setOrigin(0.5)

    continueBtn.on('pointerdown', () => {
      this.scene.start('HubScene')
    })
  }

  private updateStats(): void {
    const fuelPct = this.maxFuel > 0 ? Math.round((this.displayFuel / this.maxFuel) * 100) : 0
    const cargoPct = this.maxCargo > 0 ? Math.round((this.displayCargo / this.maxCargo) * 100) : 0
    const zone = this.getZone(this.displayDepth)

    this.depthText.setText(`Depth: ${this.displayDepth}`)
    this.fuelText.setText(`Fuel: ${Math.ceil(this.displayFuel / 100)}/${Math.ceil(this.maxFuel / 100)} (${fuelPct}%)`)
    this.cargoText.setText(`Cargo: ${cargoPct}%`)
    this.zoneText.setText(zone.name)

    if (fuelPct < 20) this.fuelText.setColor('#FF4444')
    else if (fuelPct < 50) this.fuelText.setColor('#FFAA00')
    else this.fuelText.setColor('#44FF44')
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
